/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { or, alias } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core/types/symbols';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import type Assessment from '@linc-technologies/linced-common/models/assessments/assessment';
import type Goal from '@linc-technologies/linced-common/models/goals/goal';
import type Group from '@linc-technologies/linced-common/models/group';
import type Media from '@linc-technologies/linced-common/models/media';
import type Person from '@linc-technologies/linced-common/models/person';
import type Comment from '@linc-technologies/linced-common/models/posts/comment';
import type Page from '@linc-technologies/linced-common/models/posts/page';
import type Reaction from '@linc-technologies/linced-common/models/posts/reaction';
import type Tag from '@linc-technologies/linced-common/models/posts/tag';
import type School from '@linc-technologies/linced-common/models/school';
import type Survey from '@linc-technologies/linced-common/models/surveys/survey';

// Proto: posts.Post.
export default class Post extends Model {
  // Not 'posts/post': the post subtypes (text, the notices, attendancenote)
  // extend this class with their own types.
  declare [Type]: string;

  @tracked postType: EnumValue<typeof PostType> = PostType.POST;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string;

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number', { defaultValue: 0 })
  declare displayDate: number;

  @or('displayDate', 'createTime')
  declare date: number | undefined; // Fall back to createTime if no displayDate is set.

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @belongsTo('assessments/assessment', { async: true, inverse: null })
  declare assessmentId: AsyncBelongsTo<Assessment>;

  @alias('assessmentId')
  declare assessment: AsyncBelongsTo<Assessment>;

  @alias('assessment.id')
  declare assessment_id: string | null | undefined;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @hasMany('group', { async: true, inverse: null })
  declare groupIds: AsyncHasMany<Group>;

  @alias('groupIds')
  declare groups: AsyncHasMany<Group>;

  @attr('number', { defaultValue: 0 })
  declare status: EnumValue<typeof PostStatus> | 0;

  @belongsTo('posts/page', { async: true, inverse: null })
  declare pageId: AsyncBelongsTo<Page>;

  @alias('pageId')
  declare page: AsyncBelongsTo<Page>;

  @alias('page.id')
  declare page_id: string | null | undefined;

  @attr('string', { defaultValue: '' })
  declare subject: string;

  @attr('string', { defaultValue: '' })
  declare content: string;

  @alias('content')
  declare postContent: string;

  @attr('boolean', { defaultValue: false })
  declare isPinned: boolean;

  // Proofreading status
  @belongsTo('person', { async: true, inverse: null })
  declare proofreaderId: AsyncBelongsTo<Person>;

  @alias('proofreaderId')
  declare proofreader: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare proofreadTime: number;

  // Attachments / media
  @hasMany('media', { inverse: 'postId', async: true })
  declare mediaIds: AsyncHasMany<Media>;

  @alias('mediaIds')
  declare media: AsyncHasMany<Media>;

  @hasMany('posts/tag', { async: true, inverse: null })
  declare tagIds: AsyncHasMany<Tag>;

  @alias('tagIds')
  declare tags: AsyncHasMany<Tag>;

  @array('string')
  declare customTags: StatefulArray<string>;

  @hasMany('goals/goal', { async: true, inverse: null })
  declare goalIds: AsyncHasMany<Goal>;

  @alias('goalIds')
  declare goals: AsyncHasMany<Goal>;

  /*nceaStandardIds: array('string'),
   nceaStandards: alias('nceaStandardIds'),*/

  @belongsTo('surveys/survey', { async: true, inverse: null })
  declare surveyId: AsyncBelongsTo<Survey>;

  @alias('surveyId')
  declare survey: AsyncBelongsTo<Survey>;

  @hasMany('posts/comment', { async: true, inverse: 'postId' })
  declare comments: AsyncHasMany<Comment>;

  @hasMany('posts/reaction', { async: true, inverse: 'postId' })
  declare reactions: AsyncHasMany<Reaction>;

  // Reload the related person record (to update post counts) after every
  // save. Post subtypes that don't need this (see e.g. models/posts/text.js)
  // set reloadPersonOnSave to false.
  reloadPersonOnSave = true;

  async save(...args: Parameters<Model['save']>): Promise<this> {
    const result = await super.save(...args);
    if (this.reloadPersonOnSave) {
      void (this as Post).belongsTo('personId').reload();
    }
    return result;
  }

  @computed('postType', 'person.id', 'id')
  get url() {
    const id = this.id;
    switch (this.postType) {
      case PostType.POST:
        return `/learners/${this.get('person.id') as string}/posts/${id}`;
      case PostType.COMMUNITY_NOTICE:
        return `/home/community-notices/${id}`;
      case PostType.GLOBAL_NOTICE:
        return `/home/hero-notices/${id}`;
      case PostType.STUDENT_NOTICE:
        return `/home/student-notices/${id}`;
      case PostType.ATTENDANCE_NOTE:
        return `/people/${this.get('person.id') as string}/attendance`;
      default:
        return `/posts/${id}`;
    }
  }
}
